// Unified Eastern (Chinese) Zodiac compatibility module
// NOTE: Keep canonical animal names and element interactions standardized across app and web.

import CNY_DATES from '../data/chinese_new_year_dates.json';

export const CHINESE_ANIMALS = [
  'Rat', 'Ox', 'Tiger', 'Cat', 'Dragon', 'Snake', 'Horse', 'Goat', 'Monkey', 'Rooster', 'Dog', 'Pig'
];

export const CHINESE_ELEMENTS = ['Wood', 'Fire', 'Earth', 'Metal', 'Water'];

// Triads (affinity groups)
export const triads = [
  ['Rat', 'Dragon', 'Monkey'],
  ['Ox', 'Snake', 'Rooster'],
  ['Tiger', 'Horse', 'Dog'],
  ['Cat', 'Goat', 'Pig']
];

// Secret friend (Six Harmonies 六合) pairs treated as affinity
const secretFriendPairs = new Set([
  'Rat|Ox', 'Ox|Rat',
  'Tiger|Pig', 'Pig|Tiger',
  'Cat|Dog', 'Dog|Cat',
  'Dragon|Rooster', 'Rooster|Dragon',
  'Snake|Monkey', 'Monkey|Snake',
  'Horse|Goat', 'Goat|Horse',
]);

// Synonyms normalization
const animalAliases = new Map([
  ['rabbit', 'Cat'],
  ['sheep', 'Goat'],
]);

export function normalizeAnimalName(name) {
  if (!name) return null;
  const lower = String(name).trim().toLowerCase();
  const alias = animalAliases.get(lower);
  const proper = alias || lower.charAt(0).toUpperCase() + lower.slice(1);
  return CHINESE_ANIMALS.includes(proper) ? proper : null;
}

export function getAnimalIndex(name) {
  const n = normalizeAnimalName(name);
  return n ? CHINESE_ANIMALS.indexOf(n) : -1;
}

export function areOpposites(a, b) {
  const iA = getAnimalIndex(a);
  const iB = getAnimalIndex(b);
  if (iA < 0 || iB < 0) return false;
  const d = Math.abs(iA - iB);
  return d === 6;
}

export function inTriad(a, b) {
  const A = normalizeAnimalName(a);
  const B = normalizeAnimalName(b);
  if (!A || !B) return false;
  return triads.some(group => group.includes(A) && group.includes(B));
}

function inSecretFriendPair(a, b) {
  const A = normalizeAnimalName(a);
  const B = normalizeAnimalName(b);
  if (!A || !B) return false;
  return secretFriendPairs.has(`${A}|${B}`);
}

export function getChineseElement(year) {
  const y = Number(year);
  if (Number.isNaN(y)) return null;
  const last = y % 10;
  if (last === 0 || last === 1) return 'Metal';
  if (last === 2 || last === 3) return 'Water';
  if (last === 4 || last === 5) return 'Wood';
  if (last === 6 || last === 7) return 'Fire';
  return 'Earth'; // 8,9
}

function getChineseNewYearDateForYear(year) {
  const s = CNY_DATES[String(year)];
  if (!s) return null;
  const [mm, dd] = s.split('-').map(v => parseInt(v, 10));
  return new Date(year, mm - 1, dd);
}

export function getChineseAnimal(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;

  const cny = getChineseNewYearDateForYear(y);
  if (!cny) {
    console.warn(`CNY date missing for year ${y}. Falling back to modulo. Please extend CNY JSON.`);
    return CHINESE_ANIMALS[(y - 1900) % 12];
  }

  const inputDate = new Date(y, m - 1, d);
  const effectiveYear = inputDate < cny ? y - 1 : y;
  return CHINESE_ANIMALS[(effectiveYear - 1900) % 12];
}

// Determine tier relationship between two animals
export function determineTier(a, b) {
  const A = normalizeAnimalName(a);
  const B = normalizeAnimalName(b);
  if (!A || !B) return 'neutral';
  if (A === B) return 'same';
  if (inTriad(A, B) || inSecretFriendPair(A, B)) return 'triad';
  if (areOpposites(A, B)) return 'opposite';

  // Challenging sets derived from existing data (canonicalized)
  const challengingMap = {
    Rat: ['Horse', 'Goat', 'Cat'],
    Ox: ['Horse', 'Dog', 'Goat'],
    Tiger: ['Monkey', 'Snake'],
    Cat: ['Rooster', 'Rat'],
    Dragon: ['Dog', 'Ox', 'Cat'],
    Snake: ['Tiger', 'Pig'],
    Horse: ['Rat', 'Ox'],
    Goat: ['Ox', 'Rat', 'Dog'],
    Monkey: ['Tiger', 'Pig'],
    Rooster: ['Cat', 'Dog'],
    Dog: ['Dragon', 'Goat', 'Rooster'],
    Pig: ['Snake', 'Monkey'],
  };
  const list = challengingMap[A] || [];
  if (list.includes(B) && !areOpposites(A, B)) return 'challenging';
  return 'neutral';
}

export function computeBaseAnimalScore(a, b) {
  const tier = determineTier(a, b);
  switch (tier) {
    case 'same': return 95;
    case 'triad': return 85;
    case 'opposite': return 10; // opposites baseline
    case 'challenging': return 35;
    default: return 60; // neutral
  }
}

// Element cycles
const productive = new Map([
  ['Wood', 'Fire'],
  ['Fire', 'Earth'],
  ['Earth', 'Metal'],
  ['Metal', 'Water'],
  ['Water', 'Wood'],
]);

const destructive = new Map([
  ['Wood', 'Earth'],
  ['Earth', 'Water'],
  ['Water', 'Fire'],
  ['Fire', 'Metal'],
  ['Metal', 'Wood'],
]);

export function computeElementBonus(elemA, elemB) {
  if (!elemA || !elemB) return 0;
  if (elemA === elemB) return 10;
  if (productive.get(elemA) === elemB || productive.get(elemB) === elemA) return 15;
  if (destructive.get(elemA) === elemB || destructive.get(elemB) === elemA) return -10;
  return 0;
}

function clamp0to100(x) {
  return Math.max(0, Math.min(100, x));
}

export function calculateChineseCompatibilityProfiles(profile1, profile2) {
  const animalA = normalizeAnimalName(profile1?.chineseAnimal);
  const animalB = normalizeAnimalName(profile2?.chineseAnimal);
  const elementA = profile1?.chineseElement;
  const elementB = profile2?.chineseElement;

  if (!animalA || !animalB) {
    return {
      score: 60,
      breakdown: {
        baseAnimalScore: 60,
        elementBonus: 0,
        tier: 'neutral',
        animals: { a: animalA || profile1?.chineseAnimal || null, b: animalB || profile2?.chineseAnimal || null },
        elements: { a: elementA || null, b: elementB || null },
      },
      description: 'Unknown animals provided; treated as neutral (no element bonus).',
    };
  }

  const baseAnimalScore = computeBaseAnimalScore(animalA, animalB);
  const elementBonus = computeElementBonus(elementA, elementB);
  const tier = determineTier(animalA, animalB);
  const score = clamp0to100(baseAnimalScore + elementBonus);

  const interaction = elementBonus === 15 ? 'productive element synergy (+15)'
    : elementBonus === 10 ? 'same element harmony (+10)'
    : elementBonus === -10 ? 'destructive element tension (-10)'
    : 'no element interaction (0)';

  const description = `${animalA} (${elementA || 'Unknown'}) × ${animalB} (${elementB || 'Unknown'}) • ${tier.toUpperCase()} • ${interaction}`;

  return {
    score,
    breakdown: {
      baseAnimalScore,
      elementBonus,
      tier,
      animals: { a: animalA, b: animalB },
      elements: { a: elementA || null, b: elementB || null },
    },
    description,
  };
}

export function calculateChineseCompatibilityFromDates(dateISO1, dateISO2) {
  const d1 = new Date(dateISO1);
  const d2 = new Date(dateISO2);
  const y1 = d1.getFullYear(), m1 = d1.getMonth() + 1, day1 = d1.getDate();
  const y2 = d2.getFullYear(), m2 = d2.getMonth() + 1, day2 = d2.getDate();

  const a1 = getChineseAnimal(y1, m1, day1);
  const a2 = getChineseAnimal(y2, m2, day2);
  const e1 = getChineseElement(y1);
  const e2 = getChineseElement(y2);

  return calculateChineseCompatibilityProfiles(
    { chineseAnimal: a1, chineseElement: e1 },
    { chineseAnimal: a2, chineseElement: e2 },
  );
}

export function generateChineseMatchesBase(userProfile) {
  const userAnimal = normalizeAnimalName(userProfile?.chineseAnimal);
  const results = CHINESE_ANIMALS.map(animal => {
    const baseScore = computeBaseAnimalScore(userAnimal, animal);
    const tier = determineTier(userAnimal, animal);
    return { animal, tier, baseScore };
  }).sort((a, b) => b.baseScore - a.baseScore);

  return {
    note: 'Base animal-only scores. Element bonus not applied.',
    results,
  };
}

export default {
  CHINESE_ANIMALS,
  CHINESE_ELEMENTS,
  CNY_DATES,
  triads,
  normalizeAnimalName,
  getAnimalIndex,
  areOpposites,
  inTriad,
  getChineseElement,
  getChineseAnimal,
  determineTier,
  computeBaseAnimalScore,
  computeElementBonus,
  calculateChineseCompatibilityProfiles,
  calculateChineseCompatibilityFromDates,
  generateChineseMatchesBase,
};
